import axios from 'axios'
import { shallowRef, computed } from 'vue'
import { useChatStore } from '@/stores/chat'
import { getFormatTimestamp } from '@/utils/date'
import { chatData } from '@/composables/chat/chatData'
import { worker } from '@/composables/chat/useChatWorker'
import useChatMessages from '@/composables/chat/actions/messages'
import useChatAttendances from '@/composables/chat/actions/attendances'
 
 
/**
 * Check status connection to websocket and send message to worker
 * @param {Object} content
 */
function sendWs(content) {
    const store = useChatStore()

    if (store().websocket.status !== 'connected') {
        console.error('CHATMIX: WebSocket is not connected')
        return
    }

    worker.postMessage({ type: 'wsMessage', data: content })
}

/**
 * Prepare and show modal.
 *
 * @param {String} title
 * @param {{}} component
 * @param {Boolean} fixed
 * @param {Object} extraData
 */
function openModal(title, component, fixed = false, extraData = {}) {
    setTimeout(() => {
        chatData.modal.show = true
        chatData.modal.title = title
        chatData.modal.fixed = fixed
        chatData.modal.component = shallowRef(component)
        chatData.modal.use = extraData
    }, 0)
}

/**
 * @deprecated
 */
function dispatch(data) {
    const execute = data?.v2?.type_beta || data?.v2?.type || data?.type || data?.action || null

    switch (execute) {
        case 'new_message':
            useChatMessages.receiveMessage(data.v2)
            break

        case 'new_attendance':
        case 'transfer_attendance':
            useChatAttendances.newAttendance(data.v2, execute === 'transfer_attendance')
            break

        case 'closed_attendance':
            useChatAttendances.remove(data.v2.data.id, data.v2.data.step)
            break

        default:
            console.warn('[chat] Unknown action: ' . execute, data)
    }
}

/**
 * Use formatMessage available in useChatMessages
 *
 * @param {string} message
 * @param {boolean} paragraph
 * @returns {string}
 * @deprecated
 */
const formatMessage = (message, paragraph = false) => {
    const format = message
        .replace(/\*{+}([ a-z0-9]+)\*{+}/img, '<b>$1</b>')
        .replace(/_{+}([ a-z0-9]+)_{+}/img, '<i>$1</i>')
        .replace(/~{+}([ a-z0-9]+)~{+}/img, '<s>$1</s>')
        .replace(/`{+}([ a-z0-9]+)`{+}/img, '<code>$1</code>')

    return paragraph ? `<p>${format.replace(/\n/g, '<br />')}</p>` : format
}

/**
 *
 * @param {string|number} timestamp
 * @returns {string}
 */
const hourMessage = (timestamp) => {
    timestamp = timestamp.length === 10 ? timestamp * 1000 : timestamp
    timestamp = parseInt(timestamp)

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if(timestamp < today.getTime()){
        return getFormatTimestamp(timestamp, 'pt-BR', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
        }).replace(':', 'h')  
    } else {
        return getFormatTimestamp(timestamp, 'pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
        }).replace(':', 'h')      
    }

}

/**
 * @param {string} typeMessage
 * @param {boolean} afterEqual
 * @param {boolean} beforeEqual
 * @returns {string}
 */
const defineRoundedInMessage = (typeMessage, afterEqual, beforeEqual) => {
    if (afterEqual && beforeEqual) {
        if (typeMessage === 'sent') return 'rounded-r-sm'
        if (typeMessage === 'received') return 'rounded-l-sm'
    }

    if (afterEqual) {
        if (typeMessage === 'sent') return 'rounded-br-sm'
        if (typeMessage === 'received') return 'rounded-bl-sm'
    }

    if (beforeEqual) {
        if (typeMessage === 'sent') return 'rounded-r-sm'
        if (typeMessage === 'received') return 'rounded-tl-sm'
    }

    return ''
}

const defineColorBaseadAndOriginMessage = (origin) => {
    if (origin === 'deleted') return ['bg-rose-700', 'border-t-rose-700 border-l-rose-700']
    if (origin === 'chat') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    // TODO: Replace with blue more dark
    //if (origin === 'automation') return ['bg-emerald-600', 'border-t-emerald-600 border-l-emerald-600']

     
    if (origin === 'automation') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'automatic') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'audit') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'assign_attendance') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'transfer_attendance') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'missing_attendance') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'end_attendance') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'timeout_attendance') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']
    if (origin === 'timeout_attendance_business') return ['bg-chat-primary', 'border-t-chat-primary border-l-chat-primary']

    return ['bg-neutral-400', 'border-t-neutral-400 border-l-neutral-400']
}

/**
 * @param {string} origin
 * @param {string} type
 * @returns {string}
 */
const originMessage = (origin, type) => {
    if (type === 'received') return 'Enviada pelo cliente'

    const originsAccept = {
        automation: 'pela automação',
        external: 'por dispositivo externo',
        sent_external: 'por dispositivo externo',
        chat: 'pelo chat, por um atendente',
        app: 'pelo aplicativo do chat',
        survey: 'através da pesquisa de satisfacão',
        timeout_attendance: 'através do tempo limite de atendimento',
        timeout_attendance_business: 'através do tempo limite de atendimento',
    }

    const originDetail = originsAccept[origin] || null
    if (originDetail) return `Enviada ${originDetail}`

    const internalTypes = {
        call_response: 'Registro de resposta de ligação',
        closed_attendance: 'Informação de atendimento encerrado',
        end_attendance: 'Informação de atendimento encerrado',
    }

    return internalTypes[origin] || 'Mensagem interna'
}

const scrollToBottom = () => {
    const listMessages = document.getElementById('attendanceMessages')
    if(listMessages){
        const height = listMessages.scrollHeight
        listMessages.scrollTo({ top: height })
    }
}

const format = (message) => {
    if (message && typeof message === 'object') {
        message = message.content ?? message.title ?? message.caption ?? ''
    }
    if (typeof message !== 'string') {
        message = message == null ? '' : String(message)
    }

    const tags = [
        { regex: /\*\*?(.*?)\*\*?/g, tag: 'b' },
        { regex: /\~\~(.*?)\~\~/g, tag: 's' },
        { regex: /(?:^|\s)_([^_\n]+?)_(?:$|\s)/g, tag: 'i' },
        { regex: /```(.*?)```/g, tag: 'code' },
    ]

    // Escapa qualquer HTML enviado como texto (ex.: <iframe>, <script>),
    // preservando apenas as tags de formatação geradas internamente
    // (<b>, <i>, <s>, <code>).
    const escapeHtml = (content) => {
        return content.replace(/<\/?(?:b|i|s|code)>|[&<>]/g, (match) => {
            switch (match) {
                case '<':
                    return '&lt;'

                case '>':
                    return '&gt;'

                case '&':
                    return '&amp;'

                default:
                    return match
            }
        })
    }

    message = escapeHtml(message)

    for (const tag of tags) {
        message = message.replace(tag.regex, (match, conteudo) => {
            return `<${tag.tag}>${conteudo}</${tag.tag}>`
        })
    }

    message = message.replace(/(https?:\/\/[^\s<>"']+)/g, (match) => {
        return `<a href="${match}" target="_blank">${match}</a>`
    })

    return message
}


 

export default {
    dispatch,
    sendWs,
    openModal,
    data: chatData,
    scrollToBottom,
    formatMessage,
    format, 
    hourMessage,
    originMessage,
    defineRoundedInMessage,
    defineColorBaseadAndOriginMessage,
}
